// ─── Shared styles — earthy orange palette ────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid #7a2e00', fontFamily:"'Georgia',serif", color:'#1a0e06', lineHeight:'1.7', background:'#fdf6ee' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c45a10', paddingBottom:'8px', marginBottom:'24px', color:'#7a2e00' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#a04010', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c45a10', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#fdf0e4', border:'2px solid #c45a10', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New',monospace", fontSize:'0.88rem', color:'#7a2e00', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c45a10' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#a04010', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf4ec' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #e8c89a', verticalAlign:'top', background:'#fdf6ee' },
  tdL:          { fontWeight:'600', color:'#7a2e00', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New',monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fdf4ec', border:'1px solid #e8b07a', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7a2e00', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#7a4a28', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it, i) => <div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{...S.th, width:'220px'}}>Concept</th>
        <th style={S.th}>Detail</th>
      </tr></thead>
      <tbody>{rows.map(([l, v], i) => (
        <tr key={i}>
          <td style={{...(i%2===0 ? S.tdE : S.tdO), ...S.tdL}}>{l}</td>
          <td style={{...(i%2===0 ? S.tdE : S.tdO), ...S.code}}>{v}</td>
        </tr>
      ))}</tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Omni Wheel Anatomy (single wheel, exploded callouts)
// ─────────────────────────────────────────────────────────────────────
function DiagramOmniAnatomy() {
  const W = 960, H = 420;
  const cx = 300, cy = 210, R = 110;
  const nRollers = 12;

  // Draw rollers arranged around hub
  function rollerPoints(angle, rLen, rWid) {
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const rx = cx + (R - 4) * cos, ry = cy + (R - 4) * sin;
    // roller is perpendicular to radius
    const tx = -sin, ty = cos;
    return [
      [rx + tx * rWid - cos * rLen * 0.5, ry + ty * rWid - sin * rLen * 0.5],
      [rx - tx * rWid - cos * rLen * 0.5, ry - ty * rWid - sin * rLen * 0.5],
      [rx - tx * rWid + cos * rLen * 0.5, ry - ty * rWid + sin * rLen * 0.5],
      [rx + tx * rWid + cos * rLen * 0.5, ry + ty * rWid + sin * rLen * 0.5],
    ].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Omni Wheel Anatomy — Hub, Rollers, Axis &amp; Force Directions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arOA" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arOAb" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a6496"/>
          </marker>
          <marker id="arOAg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
          <radialGradient id="hubGrad" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#e8b07a"/>
            <stop offset="100%" stopColor="#a04010"/>
          </radialGradient>
        </defs>

        {/* ── Wheel rim (outer ring) ── */}
        <circle cx={cx} cy={cy} r={R+8} fill="none" stroke="#7a2e00" strokeWidth="4"/>
        <circle cx={cx} cy={cy} r={R+8} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.12"/>

        {/* ── Rollers ── */}
        {Array.from({length: nRollers}, (_, i) => {
          const angle = (2 * Math.PI * i) / nRollers;
          return (
            <polygon key={i}
              points={rollerPoints(angle, 22, 7)}
              fill={i % 2 === 0 ? '#c45a10' : '#d4874a'}
              stroke="#7a2e00" strokeWidth="1.5"
              opacity="0.9"/>
          );
        })}

        {/* ── Hub (spoke wheel) ── */}
        <circle cx={cx} cy={cy} r={38} fill="url(#hubGrad)" stroke="#7a2e00" strokeWidth="2.5"/>
        {[0, 60, 120, 180, 240, 300].map((deg, i) => {
          const a = deg * Math.PI / 180;
          return <line key={i}
            x1={cx + 10 * Math.cos(a)} y1={cy + 10 * Math.sin(a)}
            x2={cx + 36 * Math.cos(a)} y2={cy + 36 * Math.sin(a)}
            stroke="#7a2e00" strokeWidth="2" opacity="0.6"/>;
        })}
        <circle cx={cx} cy={cy} r={10} fill="#7a2e00"/>

        {/* ── Drive axis (horizontal) ── */}
        <line x1={cx - R - 50} y1={cy} x2={cx - R - 10} y2={cy}
          stroke="#7a2e00" strokeWidth="3.5"/>
        <line x1={cx + R + 10} y1={cy} x2={cx + R + 50} y2={cy}
          stroke="#7a2e00" strokeWidth="3.5"/>

        {/* ── Force arrows ── */}
        {/* Drive force (motor torque → forward) */}
        <line x1={cx} y1={cy + R + 50} x2={cx} y2={cy + R + 15}
          stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arOA)"/>
        <text x={cx} y={cy + R + 66} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#c45a10">Drive force F_drive</text>
        <text x={cx} y={cy + R + 78} textAnchor="middle" fontSize="8.5" fill="#a04010">(from motor torque)</text>

        {/* Free-roll direction (lateral) */}
        <line x1={cx - R - 40} y1={cy - 60} x2={cx - R - 10} y2={cy - 60}
          stroke="#2a6496" strokeWidth="2" markerEnd="url(#arOAb)"/>
        <text x={cx - R - 44} y={cy - 64} textAnchor="end" fontSize="9" fontWeight="700" fill="#2a6496">Free-roll</text>
        <text x={cx - R - 44} y={cy - 52} textAnchor="end" fontSize="8" fill="#2a6496">(zero friction lateral)</text>

        {/* ── Callout boxes ── */}
        {/* Roller callout */}
        <line x1={cx + (R-4) * Math.cos(Math.PI/6)} y1={cy + (R-4) * Math.sin(Math.PI/6)}
          x2={cx + 160} y2={cy - 10} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={cx + 158} y={cy - 40} width={185} height={38} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx + 250} y={cy - 22} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Barrel roller</text>
        <text x={cx + 250} y={cy - 9} textAnchor="middle" fontSize="8.5" fill="#a04010">Rotates freely on its own axis · tangential to rim</text>

        {/* Hub callout */}
        <line x1={cx + 38} y1={cy} x2={cx + 160} y2={cy + 55} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx + 158} y={cy + 40} width={185} height={38} rx={4}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx + 250} y={cy + 58} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Hub / rim plate</text>
        <text x={cx + 250} y={cy + 71} textAnchor="middle" fontSize="8.5" fill="#a04010">Rigid disc driven by motor shaft · carries roller axles</text>

        {/* Rim callout */}
        <line x1={cx + (R+8) * Math.cos(-Math.PI*0.3)} y1={cy + (R+8) * Math.sin(-Math.PI*0.3)}
          x2={cx + 165} y2={cy - 100} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx + 163} y={cy - 130} width={185} height={38} rx={4}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx + 255} y={cy - 112} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Wheel rim (outer)</text>
        <text x={cx + 255} y={cy - 99} textAnchor="middle" fontSize="8.5" fill="#a04010">Rollers tangent to rim · contact patch rolls freely</text>

        {/* Axis callout */}
        <line x1={cx - R - 30} y1={cy} x2={cx - R - 30} y2={cy - 55} stroke="#7a2e00" strokeWidth="1.2"/>
        <rect x={cx - R - 175} y={cy - 90} width={148} height={38} rx={4}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx - R - 101} y={cy - 72} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Drive axle</text>
        <text x={cx - R - 101} y={cy - 59} textAnchor="middle" fontSize="8.5" fill="#a04010">Motor couples here · one DOF constrained</text>

        {/* Right facts panel */}
        <rect x={648} y={10} width={304} height={H-20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={800} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Omni Wheel — Key Facts</text>
        <line x1={656} y1={34} x2={944} y2={34} stroke="#e8b07a"/>
        {[
          ['Roller axis:',     'Tangential to rim (90° to drive)'],
          ['Roller count:',    '8–16 typical · more = smoother'],
          ['Roller material:', 'Polyurethane, rubber, nylon'],
          ['DOF provided:',    '1 actuated + 1 passive (lateral)'],
          ['',                 ''],
          ['Drive direction:', 'Perpendicular to roller axis'],
          ['Free-roll dir.:',  'Along roller axis (no resistance)'],
          ['',                 ''],
          ['Floor contact:',   'One roller at a time (bumpy)'],
          ['Load capacity:',   'Limited by single roller contact'],
          ['',                 ''],
          ['Variants:',        ''],
          ['  Single-row:',    'One ring of rollers (standard)'],
          ['  Double-row:',    'Two offset rows (smoother ride)'],
          ['  45° rollers:',   'Rare — alters force geometry'],
          ['',                 ''],
          ['Applications:',    ''],
          ['',                 'Holonomic robot bases (3-wheel)'],
          ['',                 'Material handling turntables'],
          ['',                 'Conveyor corner transfers'],
          ['',                 'Wheelchair mobility aids'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={658} y={48+i*14} fontSize="9" fontWeight={l && v==='' ? '700' : '400'} fill={l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l && v ? 752 : 658} y={48+i*14} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The omni wheel's barrel rollers are oriented tangentially, so the wheel drives in one direction while rolling freely in the perpendicular direction. Each roller contacts the floor independently.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Mecanum Wheel Anatomy (45° rollers, force decomposition)
// ─────────────────────────────────────────────────────────────────────
function DiagramMecanumAnatomy() {
  const W = 960, H = 420;
  const cx = 290, cy = 210, R = 105;
  const nRollers = 10;

  // Mecanum rollers are at 45° to the wheel axis
  function mecanumRollerPts(angle, rLen, rWid) {
    const cos45 = Math.cos(Math.PI / 4);
    const sin45 = Math.sin(Math.PI / 4);
    const rx = cx + (R - 2) * Math.cos(angle);
    const ry = cy + (R - 2) * Math.sin(angle);
    // roller long axis at 45° to wheel tangent
    const tang = angle + Math.PI / 2; // tangential direction
    const ra = tang + Math.PI / 4;    // roller axis = 45° from tangent
    const tc = Math.cos(ra), ts = Math.sin(ra);
    const nc = Math.cos(ra + Math.PI / 2), ns = Math.sin(ra + Math.PI / 2);
    return [
      [rx + tc * rLen * 0.5 + nc * rWid, ry + ts * rLen * 0.5 + ns * rWid],
      [rx - tc * rLen * 0.5 + nc * rWid, ry - ts * rLen * 0.5 + ns * rWid],
      [rx - tc * rLen * 0.5 - nc * rWid, ry - ts * rLen * 0.5 - ns * rWid],
      [rx + tc * rLen * 0.5 - nc * rWid, ry + ts * rLen * 0.5 - ns * rWid],
    ].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mecanum Wheel Anatomy — 45° Rollers, Force Decomposition &amp; Left/Right Variants</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arMA" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arMAb" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a6496"/>
          </marker>
          <marker id="arMAg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
          <radialGradient id="hubGrad2" cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#e8b07a"/>
            <stop offset="100%" stopColor="#a04010"/>
          </radialGradient>
        </defs>

        {/* Rim */}
        <circle cx={cx} cy={cy} r={R+8} fill="#d4874a" stroke="#7a2e00" strokeWidth="3" opacity="0.15"/>
        <circle cx={cx} cy={cy} r={R+8} fill="none" stroke="#7a2e00" strokeWidth="3.5"/>

        {/* 45° rollers */}
        {Array.from({length: nRollers}, (_, i) => {
          const angle = (2 * Math.PI * i) / nRollers;
          return (
            <polygon key={i}
              points={mecanumRollerPts(angle, 26, 8)}
              fill={i % 2 === 0 ? '#c45a10' : '#d4874a'}
              stroke="#7a2e00" strokeWidth="1.5"
              opacity="0.9"/>
          );
        })}

        {/* Hub */}
        <circle cx={cx} cy={cy} r={36} fill="url(#hubGrad2)" stroke="#7a2e00" strokeWidth="2.5"/>
        {[0, 60, 120, 180, 240, 300].map((deg, i) => {
          const a = deg * Math.PI / 180;
          return <line key={i}
            x1={cx + 10 * Math.cos(a)} y1={cy + 10 * Math.sin(a)}
            x2={cx + 34 * Math.cos(a)} y2={cy + 34 * Math.sin(a)}
            stroke="#7a2e00" strokeWidth="2" opacity="0.6"/>;
        })}
        <circle cx={cx} cy={cy} r={10} fill="#7a2e00"/>

        {/* Drive axle */}
        <line x1={cx - R - 50} y1={cy} x2={cx - R - 8} y2={cy} stroke="#7a2e00" strokeWidth="3.5"/>
        <line x1={cx + R + 8} y1={cy} x2={cx + R + 50} y2={cy} stroke="#7a2e00" strokeWidth="3.5"/>

        {/* 45° roller axis indicator on one roller */}
        {(() => {
          const a = -Math.PI * 0.15;
          const rx = cx + (R - 2) * Math.cos(a);
          const ry = cy + (R - 2) * Math.sin(a);
          const ra = a + Math.PI / 2 + Math.PI / 4;
          return (
            <g>
              <line x1={rx - Math.cos(ra)*28} y1={ry - Math.sin(ra)*28}
                x2={rx + Math.cos(ra)*28} y2={ry + Math.sin(ra)*28}
                stroke="#2a6496" strokeWidth="2" strokeDasharray="4,3"/>
              <text x={rx + Math.cos(ra)*32} y={ry + Math.sin(ra)*32}
                fontSize="8.5" fontWeight="700" fill="#2a6496">45°</text>
            </g>
          );
        })()}

        {/* Force decomposition arrows at contact patch */}
        {/* Drive force (vertical = forward) */}
        <line x1={cx} y1={cy + R + 60} x2={cx} y2={cy + R + 18}
          stroke="#c45a10" strokeWidth="2.5" markerEnd="url(#arMA)"/>
        <text x={cx} y={cy + R + 76} textAnchor="middle" fontSize="9" fontWeight="700" fill="#c45a10">F_drive (↑)</text>

        {/* Lateral force component */}
        <line x1={cx + 10} y1={cy + R + 18} x2={cx + 60} y2={cy + R + 18}
          stroke="#2a6496" strokeWidth="2" markerEnd="url(#arMAb)"/>
        <text x={cx + 64} y={cy + R + 22} fontSize="8.5" fontWeight="700" fill="#2a6496">F_lateral</text>

        {/* 45° resultant */}
        <line x1={cx} y1={cy + R + 60} x2={cx + 55} y2={cy + R + 5}
          stroke="#2a7a3b" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#arMAg)"/>
        <text x={cx + 58} y={cy + R + 8} fontSize="8.5" fontWeight="700" fill="#2a7a3b">Roller reaction (45°)</text>

        {/* Callouts */}
        {/* 45° roller label */}
        <line x1={cx + (R-2)*Math.cos(0.6)} y1={cy + (R-2)*Math.sin(0.6)}
          x2={cx + 165} y2={cy - 20} stroke="#c45a10" strokeWidth="1.2"/>
        <rect x={cx + 163} y={cy - 50} width={200} height={38} rx={4}
          fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={cx + 263} y={cy - 32} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">45° barrel roller</text>
        <text x={cx + 263} y={cy - 19} textAnchor="middle" fontSize="8.5" fill="#a04010">Set at 45° to wheel axis · generates lateral force</text>

        {/* L/R variant note */}
        <rect x={cx + 163} y={cy + 30} width={200} height={52} rx={4}
          fill="#fdf0e4" stroke="#7a2e00" strokeWidth="1.5"/>
        <text x={cx + 263} y={cy + 46} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Left / Right variants</text>
        <text x={cx + 263} y={cy + 59} textAnchor="middle" fontSize="8.5" fill="#a04010">Mirror-image roller angle (/ vs \)</text>
        <text x={cx + 263} y={cy + 72} textAnchor="middle" fontSize="8.5" fill="#a04010">Must pair correctly on 4-wheel chassis</text>

        {/* Right info panel */}
        <rect x={648} y={10} width={304} height={H-20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={800} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Mecanum Wheel — Key Facts</text>
        <line x1={656} y1={34} x2={944} y2={34} stroke="#e8b07a"/>
        {[
          ['Roller angle:',   '45° to wheel spin axis'],
          ['Roller count:',   '8–16 per wheel'],
          ['Variants:',       'Left-hand (/) · Right-hand (\\)'],
          ['',                ''],
          ['Force split:',    'F_drive decomposes at 45°'],
          ['',                'F_fwd = F_drive × cos45°'],
          ['',                'F_lat = F_drive × sin45°'],
          ['',                '= F_drive × 0.707 each'],
          ['',                ''],
          ['Net motion:',     'Sum of all 4 wheel vectors'],
          ['',                'Lateral: opposite pairs cancel fwd'],
          ['',                'fwd/rev cancel lat → pure slide'],
          ['',                ''],
          ['Load contact:',   'Multiple rollers simultaneously'],
          ['',                '→ higher load than omni'],
          ['',                ''],
          ['Efficiency:',     '~70–80% vs conventional wheel'],
          ['Floor req.:',     'Flat, hard, clean surface'],
          ['',                ''],
          ['Applications:',   ''],
          ['',                'Industrial AGV / warehouse robots'],
          ['',                'Hospital bed & wheelchair movers'],
          ['',                'Forklift lateral positioning'],
        ].map(([l, v], i) => (
          <g key={i}>
            <text x={658} y={48+i*14} fontSize="9" fontWeight={l && v==='' ? '700' : '400'} fill={l ? '#7a2e00' : '#7a4a28'}>{l}</text>
            <text x={l && v ? 750 : 658} y={48+i*14} fontSize="9" fontFamily="'Courier New',monospace" fill="#a04010">{v}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Mecanum rollers at 45° split each wheel's drive force into equal forward and lateral components. The four-wheel arrangement combines these vectors to produce motion in any direction without rotating the chassis.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 3 — 4-Wheel Mecanum Drive Patterns (top-down chassis view)
// Shows 6 motion modes with wheel rotation directions
// ─────────────────────────────────────────────────────────────────────
function DiagramMecanumModes() {
  const W = 960, H = 500;

  // Each mode: [title, FL, FR, RL, RR] where value = 1(fwd), -1(rev), 0(stop)
  // and motionArrow: [dx, dy] normalised
  const modes = [
    { title:'Forward',      wheels:[1,1,1,1],   arrow:[0,-1],    col:'#2a7a3b' },
    { title:'Reverse',      wheels:[-1,-1,-1,-1],arrow:[0,1],    col:'#c0392b' },
    { title:'Strafe Right', wheels:[1,-1,-1,1],  arrow:[1,0],    col:'#2a6496' },
    { title:'Strafe Left',  wheels:[-1,1,1,-1],  arrow:[-1,0],   col:'#2a6496' },
    { title:'Rotate CW',    wheels:[1,-1,1,-1],  arrow:[0,0],    col:'#a04010', spin:'cw' },
    { title:'Rotate CCW',   wheels:[-1,1,-1,1],  arrow:[0,0],    col:'#a04010', spin:'ccw' },
    { title:'Diagonal NE',  wheels:[1,0,0,1],    arrow:[1,-1],   col:'#7a2e00' },
    { title:'Diagonal NW',  wheels:[0,1,1,0],    arrow:[-1,-1],  col:'#7a2e00' },
  ];

  const cols = 4, rows = 2;
  const cellW = W / cols, cellH = H / rows;
  const chassisW = 68, chassisH = 82;
  const wheelW = 18, wheelH = 26;

  // Wheel positions relative to chassis centre: [x, y, isLeft]
  const wheelPos = [
    [-chassisW/2 - wheelW/2 + 2, -chassisH/2 + 10, true],  // FL
    [ chassisW/2 + wheelW/2 - 2, -chassisH/2 + 10, false], // FR
    [-chassisW/2 - wheelW/2 + 2,  chassisH/2 - 10, true],  // RL
    [ chassisW/2 + wheelW/2 - 2,  chassisH/2 - 10, false], // RR
  ];

  function wheelColor(val) {
    if (val === 1)  return '#2a7a3b';
    if (val === -1) return '#c0392b';
    return '#c8b89a';
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mecanum Drive — 8 Motion Modes (top-down view · green=forward · red=reverse · grey=stopped)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arMD" markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#7a2e00"/>
          </marker>
          <marker id="arMDg" markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#2a7a3b"/>
          </marker>
          <marker id="arMDr" markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#c0392b"/>
          </marker>
          <marker id="arMDb" markerWidth="8" markerHeight="8" refX="5" refY="4" orient="auto">
            <polygon points="0 0,7 4,0 8" fill="#2a6496"/>
          </marker>
        </defs>

        {modes.map((mode, mi) => {
          const col = mi % cols, row = Math.floor(mi / cols);
          const ox = col * cellW + cellW / 2;
          const oy = row * cellH + cellH / 2;

          // Cell background
          return (
            <g key={mi}>
              <rect x={col*cellW+4} y={row*cellH+4} width={cellW-8} height={cellH-8}
                rx={6} fill="#fdf6ee" stroke="#e8b07a" strokeWidth="1"/>

              {/* Title */}
              <text x={ox} y={row*cellH+22} textAnchor="middle"
                fontSize="10" fontWeight="700" fill={mode.col}>{mode.title}</text>

              {/* Chassis body */}
              <rect x={ox - chassisW/2} y={oy - chassisH/2}
                width={chassisW} height={chassisH}
                rx={6} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.5"/>

              {/* Direction label inside chassis */}
              <text x={ox} y={oy + 5} textAnchor="middle" fontSize="7.5" fill="#7a2e00" fontWeight="700">FRONT</text>

              {/* Wheels */}
              {wheelPos.map(([wx, wy, isLeft], wi) => {
                const wval = mode.wheels[wi];
                const wcx = ox + wx, wcy = oy + wy;
                const isLeftWheel = isLeft;
                // Roller diagonal: left wheels have \ rollers, right wheels have / rollers
                const ra = isLeftWheel ? Math.PI / 4 : -Math.PI / 4;
                return (
                  <g key={wi}>
                    <rect x={wcx - wheelW/2} y={wcy - wheelH/2}
                      width={wheelW} height={wheelH}
                      rx={3} fill={wheelColor(wval)} stroke="#7a2e00" strokeWidth="1.5" opacity="0.85"/>
                    {/* Roller direction line */}
                    <line
                      x1={wcx - Math.cos(ra)*10} y1={wcy - Math.sin(ra)*10}
                      x2={wcx + Math.cos(ra)*10} y2={wcy + Math.sin(ra)*10}
                      stroke="#fdf0e4" strokeWidth="1.5" opacity="0.8"/>
                    {/* Rotation indicator arrow */}
                    {wval !== 0 && (
                      <text x={wcx} y={wcy + 4} textAnchor="middle"
                        fontSize="10" fill="#fff" fontWeight="700">
                        {wval === 1 ? '↑' : '↓'}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Motion arrow */}
              {mode.arrow[0] !== 0 || mode.arrow[1] !== 0 ? (
                <g>
                  <line
                    x1={ox} y1={oy}
                    x2={ox + mode.arrow[0] * 44} y2={oy + mode.arrow[1] * 44}
                    stroke={mode.col} strokeWidth="3"
                    markerEnd={`url(#arMD${mode.col === '#2a7a3b' ? 'g' : mode.col === '#c0392b' ? 'r' : mode.col === '#2a6496' ? 'b' : ''})`}
                    opacity="0.85"/>
                </g>
              ) : (
                /* Spin indicator */
                <g>
                  <text x={ox} y={oy - 20} textAnchor="middle" fontSize="18" fill={mode.col} opacity="0.9">
                    {mode.spin === 'cw' ? '↻' : '↺'}
                  </text>
                </g>
              )}

              {/* Wheel labels */}
              {['FL','FR','RL','RR'].map((lbl, wi) => {
                const [wx, wy] = wheelPos[wi];
                return (
                  <text key={wi} x={ox + wx} y={oy + wy + wheelH/2 + 11}
                    textAnchor="middle" fontSize="7" fill="#7a4a28">{lbl}</text>
                );
              })}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Each mecanum motion mode is achieved by independently controlling four wheel directions. Strafe motion requires opposite wheel pairs to cancel forward components while adding lateral components.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 4 — 3-Wheel Omni Drive Configuration (top-down)
// Shows 120° spacing and holonomic force vectors
// ─────────────────────────────────────────────────────────────────────
function DiagramOmniDrive() {
  // W=1100: 3-wheel left (cx=170), panel centre (x=380 w=200), 4-wheel right (cx=780)
  // All elements verified non-overlapping before rendering
  const W = 1100, H = 420;
  const cx3 = 170, cy = 210, R3 = 130;   // 3-wheel config centre
  const cx4 = 800, cy4 = 210, R4 = 120;  // 4-wheel config centre

  const wheelAngles3 = [90, 210, 330].map(d => d * Math.PI / 180);
  const wheelAngles4 = [0, 90, 180, 270].map(d => d * Math.PI / 180);

  // Draw a single omni wheel as a polygon with roller lines
  function drawOmniWheel(ax, ay, wheelAngle, label, col) {
    const cos = Math.cos(wheelAngle), sin = Math.sin(wheelAngle);
    const tx = -sin, ty = cos;
    const wHalf = 18, wLen = 36;
    const corners = [
      [ax + tx*wHalf - cos*wLen/2, ay + ty*wHalf - sin*wLen/2],
      [ax - tx*wHalf - cos*wLen/2, ay - ty*wHalf - sin*wLen/2],
      [ax - tx*wHalf + cos*wLen/2, ay - ty*wHalf + sin*wLen/2],
      [ax + tx*wHalf + cos*wLen/2, ay + ty*wHalf + sin*wLen/2],
    ].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');

    // Tangential drive force arrow
    const fx = tx * 52, fy = ty * 52;

    // Label offset: push outward from wheel centre
    const labelDist = 58;
    const lx = ax + cos * labelDist;
    const ly = ay + sin * labelDist + 4;

    return (
      <g>
        <polygon points={corners} fill={col} stroke="#7a2e00" strokeWidth="2" opacity="0.85"/>
        {[-2,-1,0,1,2].map(ri => (
          <line key={ri}
            x1={ax + cos*(ri*6.5) - tx*wHalf*0.8} y1={ay + sin*(ri*6.5) - ty*wHalf*0.8}
            x2={ax + cos*(ri*6.5) + tx*wHalf*0.8} y2={ay + sin*(ri*6.5) + ty*wHalf*0.8}
            stroke="#fdf0e4" strokeWidth="1.2" opacity="0.7"/>
        ))}
        <line x1={ax} y1={ay} x2={ax+fx} y2={ay+fy}
          stroke={col} strokeWidth="2.5" markerEnd="url(#arOD)" opacity="0.9"/>
        <text x={lx} y={ly} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">{label}</text>
      </g>
    );
  }

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Omni-Wheel Drive Configurations — 3-Wheel (120°) and 4-Wheel (90°) Holonomic Bases</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arOD" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arODg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
        </defs>

        {/* ══ LEFT: 3-wheel 120° ══
            cx3=170, R3=130 → wheels reach max x=170+130=300
            Comparison panel starts at x=380 → 80px clear gap ✓ */}
        <text x={cx3} y={18} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">3-Wheel · 120° Spacing</text>

        {/* Triangle body */}
        <polygon
          points={wheelAngles3.map(a => `${(cx3+R3*0.45*Math.cos(a)).toFixed(1)},${(cy+R3*0.45*Math.sin(a)).toFixed(1)}`).join(' ')}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.35"/>
        <circle cx={cx3} cy={cy} r={R3*0.38} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5" opacity="0.7"/>
        <text x={cx3} y={cy+4} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Robot</text>

        {/* Spokes */}
        {wheelAngles3.map((a, i) => (
          <line key={i}
            x1={cx3+R3*0.38*Math.cos(a)} y1={cy+R3*0.38*Math.sin(a)}
            x2={cx3+R3*0.78*Math.cos(a)} y2={cy+R3*0.78*Math.sin(a)}
            stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>
        ))}

        {/* Wheels */}
        {wheelAngles3.map((a, i) => {
          const wx = cx3 + R3 * Math.cos(a);
          const wy = cy + R3 * Math.sin(a);
          return <g key={i}>{drawOmniWheel(wx, wy, a, ['W1','W2','W3'][i], '#c45a10')}</g>;
        })}

        {/* Net motion arrow */}
        <line x1={cx3} y1={cy} x2={cx3} y2={cy-72}
          stroke="#2a7a3b" strokeWidth="3" markerEnd="url(#arODg)"/>
        <text x={cx3+10} y={cy-52} fontSize="8.5" fontWeight="700" fill="#2a7a3b">Net motion</text>

        {/* 120° arc annotation */}
        <text x={cx3+14} y={cy+30} fontSize="8" fill="#7a4a28">120°</text>
        <path d={`M ${cx3+28} ${cy} A 28 28 0 0 1 ${(cx3+28*Math.cos(2*Math.PI/3)).toFixed(1)} ${(cy+28*Math.sin(2*Math.PI/3)).toFixed(1)}`}
          fill="none" stroke="#a04010" strokeWidth="1.2" strokeDasharray="3,2"/>

        {/* ══ COMPARISON PANEL — centre, x=380..580, clear of both configs ══ */}
        <rect x={380} y={28} width={210} height={360} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={485} y={46} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">Config Comparison</text>
        <line x1={388} y1={52} x2={582} y2={52} stroke="#e8b07a"/>

        {/* Comparison rows — label on left, value indented */}
        {[
          { l:'3-wheel 120°:',  v:'',                  head:true  },
          { l:'Pros:',          v:'Lighter, compact',   sub:true   },
          { l:'',               v:'Any heading motion', sub:false  },
          { l:'',               v:'Simple kinematics',  sub:false  },
          { l:'Cons:',          v:'Less stable',        sub:true   },
          { l:'',               v:'Lower payload',      sub:false  },
          { l:'',               v:'Forces unequal',     sub:false  },
          { l:'',               v:'',                   sub:false  },
          { l:'4-wheel 90°:',   v:'',                  head:true  },
          { l:'Pros:',          v:'More stable',        sub:true   },
          { l:'',               v:'Higher payload',     sub:false  },
          { l:'',               v:'Symmetric forces',   sub:false  },
          { l:'Cons:',          v:'Larger footprint',   sub:true   },
          { l:'',               v:'More motors',        sub:false  },
          { l:'',               v:'',                   sub:false  },
          { l:'Both configs:',  v:'',                  head:true  },
          { l:'',               v:'Holonomic motion',   sub:false  },
          { l:'',               v:'Exact X/Y/θ ctrl',  sub:false  },
          { l:'',               v:'No skid steering',   sub:false  },
          { l:'',               v:'Smooth omnidirec.',  sub:false  },
        ].map(({ l, v, head, sub }, i) => {
          const y = 66 + i * 15;
          if (head) {
            // Section header: full-width bold
            return <text key={i} x={390} y={y} fontSize="9" fontWeight="700" fill="#7a2e00">{l}</text>;
          }
          if (sub) {
            // Sub-label + value on same line: label bold left, value right
            return (
              <g key={i}>
                <text x={396} y={y} fontSize="8.5" fontWeight="700" fill="#a04010">{l}</text>
                <text x={432} y={y} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#7a4a28">{v}</text>
              </g>
            );
          }
          // Value-only line (indented)
          return (
            <text key={i} x={396} y={y} fontSize="8.5" fontFamily="'Courier New',monospace" fill="#7a4a28">{v}</text>
          );
        })}

        {/* ══ RIGHT: 4-wheel 90° ══
            cx4=800, R4=120 → wheels reach min x=800-120=680
            Comparison panel ends at x=590 → 90px clear gap ✓ */}
        <text x={cx4} y={18} textAnchor="middle" fontSize="10.5" fontWeight="700" fill="#7a2e00">4-Wheel · 90° Spacing</text>

        {/* Square body */}
        <rect x={cx4-55} y={cy4-55} width={110} height={110} rx={8}
          fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.3"/>
        <circle cx={cx4} cy={cy4} r={R4*0.38} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5" opacity="0.7"/>
        <text x={cx4} y={cy4+4} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">Robot</text>

        {/* Spokes */}
        {wheelAngles4.map((a, i) => (
          <line key={i}
            x1={cx4+R4*0.38*Math.cos(a)} y1={cy4+R4*0.38*Math.sin(a)}
            x2={cx4+R4*0.72*Math.cos(a)} y2={cy4+R4*0.72*Math.sin(a)}
            stroke="#7a2e00" strokeWidth="1.5" opacity="0.5"/>
        ))}

        {/* 4 omni wheels */}
        {wheelAngles4.map((a, i) => {
          const wx = cx4 + R4 * Math.cos(a);
          const wy = cy4 + R4 * Math.sin(a);
          return <g key={i}>{drawOmniWheel(wx, wy, a, ['W1','W2','W3','W4'][i], '#c45a10')}</g>;
        })}

        {/* Net motion arrow */}
        <line x1={cx4} y1={cy4} x2={cx4} y2={cy4-68}
          stroke="#2a7a3b" strokeWidth="3" markerEnd="url(#arODg)"/>
        <text x={cx4+10} y={cy4-48} fontSize="8.5" fontWeight="700" fill="#2a7a3b">Net motion</text>

        {/* 90° arc annotation */}
        <text x={cx4+14} y={cy4+22} fontSize="8" fill="#7a4a28">90°</text>
        <path d={`M ${cx4+22} ${cy4} A 22 22 0 0 1 ${cx4} ${cy4+22}`}
          fill="none" stroke="#a04010" strokeWidth="1.2" strokeDasharray="3,2"/>
      </svg>
      <div style={S.dCaption}>
        Three-wheel 120° configurations use fewer motors but produce asymmetric force at some headings. Four-wheel 90° arrangements give symmetric force capacity in all directions at the cost of a larger chassis.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Kinematic Equations (mecanum inverse kinematics visual)
// Force vector decomposition diagram + equation table
// ─────────────────────────────────────────────────────────────────────
function DiagramKinematics() {
  const W = 960, H = 370;

  // Top-down chassis showing the geometry parameters
  const cx = 260, cy = 190;
  const L = 80; // half wheelbase
  const W2 = 60; // half track

  // Wheel positions (FL, FR, RL, RR)
  const wp = [
    { x: cx - W2, y: cy - L, label:'FL', sign:'+', col:'#c45a10' },
    { x: cx + W2, y: cy - L, label:'FR', sign:'-', col:'#2a6496' },
    { x: cx - W2, y: cy + L, label:'RL', sign:'-', col:'#2a6496' },
    { x: cx + W2, y: cy + L, label:'RR', sign:'+', col:'#c45a10' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mecanum Drive Kinematics — Inverse Kinematics &amp; Wheel Speed Equations</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arKM" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
          <marker id="arKMb" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a6496"/>
          </marker>
          <marker id="arKMg" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#2a7a3b"/>
          </marker>
        </defs>

        {/* Chassis */}
        <rect x={cx-W2-20} y={cy-L-20} width={(W2+20)*2} height={(L+20)*2}
          rx={8} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.25"/>

        {/* CoM */}
        <circle cx={cx} cy={cy} r={6} fill="#7a2e00"/>
        <text x={cx+10} y={cy-8} fontSize="8.5" fill="#7a2e00" fontWeight="700">CoM</text>

        {/* Wheelbase and track annotations */}
        {/* L dimension */}
        <line x1={cx+W2+28} y1={cy-L} x2={cx+W2+28} y2={cy+L}
          stroke="#7a4a28" strokeWidth="1.2" markerEnd="url(#arKM)" markerStart="url(#arKM)"/>
        <text x={cx+W2+34} y={cy+4} fontSize="8.5" fontWeight="700" fill="#7a4a28">2L</text>
        <text x={cx+W2+34} y={cy+16} fontSize="7.5" fill="#7a4a28">(wheelbase)</text>

        {/* W dimension */}
        <line x1={cx-W2} y1={cy-L-32} x2={cx+W2} y2={cy-L-32}
          stroke="#7a4a28" strokeWidth="1.2" markerEnd="url(#arKM)" markerStart="url(#arKM)"/>
        <text x={cx} y={cy-L-36} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a4a28">2W (track)</text>

        {/* Wheels */}
        {wp.map((w, i) => (
          <g key={i}>
            <rect x={w.x-14} y={w.y-20} width={28} height={40}
              rx={3} fill={w.col} stroke="#7a2e00" strokeWidth="1.8" opacity="0.8"/>
            {/* Roller diagonal */}
            <line x1={w.x-10} y1={w.y+(w.sign==='+'?-10:10)}
              x2={w.x+10} y2={w.y+(w.sign==='+'?10:-10)}
              stroke="#fdf0e4" strokeWidth="1.5"/>
            <text x={w.x} y={w.y+32} textAnchor="middle" fontSize="8" fontWeight="700" fill="#7a2e00">{w.label}</text>
          </g>
        ))}

        {/* Vx, Vy, ω arrows from CoM */}
        <line x1={cx} y1={cy} x2={cx+55} y2={cy}
          stroke="#c45a10" strokeWidth="2" markerEnd="url(#arKM)"/>
        <text x={cx+58} y={cy+4} fontSize="9" fontWeight="700" fill="#c45a10">Vx</text>

        <line x1={cx} y1={cy} x2={cx} y2={cy-55}
          stroke="#2a6496" strokeWidth="2" markerEnd="url(#arKMb)"/>
        <text x={cx+4} y={cy-58} fontSize="9" fontWeight="700" fill="#2a6496">Vy</text>

        <path d={`M ${cx+18} ${cy-12} A 22 22 0 0 1 ${cx+12} ${cy+18}`}
          fill="none" stroke="#2a7a3b" strokeWidth="2" markerEnd="url(#arKMg)"/>
        <text x={cx+32} y={cy+10} fontSize="9" fontWeight="700" fill="#2a7a3b">ω</text>

        {/* Equation panel */}
        <rect x={355} y={10} width={598} height={H-20} rx={6} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.5"/>
        <text x={654} y={28} textAnchor="middle" fontSize="11" fontWeight="700" fill="#7a2e00">Mecanum Inverse Kinematics</text>
        <line x1={363} y1={34} x2={945} y2={34} stroke="#e8b07a"/>

        {/* Main equations */}
        {[
          { y: 52,  text: 'Given: desired robot velocity (Vx, Vy) and angular rate (ω)', col:'#7a2e00', bold:false },
          { y: 65,  text: 'Find:  wheel speeds ω_FL, ω_FR, ω_RL, ω_RR', col:'#7a2e00', bold:false },
          { y: 84,  text: 'ω_FL = (1/r) × ( Vx − Vy − (L+W)·ω )', col:'#c45a10', bold:true, mono:true },
          { y: 101, text: 'ω_FR = (1/r) × ( Vx + Vy + (L+W)·ω )', col:'#2a6496', bold:true, mono:true },
          { y: 118, text: 'ω_RL = (1/r) × ( Vx + Vy − (L+W)·ω )', col:'#2a6496', bold:true, mono:true },
          { y: 135, text: 'ω_RR = (1/r) × ( Vx − Vy + (L+W)·ω )', col:'#c45a10', bold:true, mono:true },
          { y: 155, text: 'r  = wheel radius (m)', col:'#7a4a28', bold:false },
          { y: 168, text: 'L  = half wheelbase (front-to-back, m)', col:'#7a4a28', bold:false },
          { y: 181, text: 'W  = half track width (left-to-right, m)', col:'#7a4a28', bold:false },
          { y: 194, text: 'Vx = desired lateral velocity (m/s)', col:'#7a4a28', bold:false },
          { y: 207, text: 'Vy = desired forward velocity (m/s)', col:'#7a4a28', bold:false },
          { y: 220, text: 'ω  = desired angular velocity (rad/s)', col:'#7a4a28', bold:false },
        ].map((eq, i) => (
          <text key={i} x={368} y={eq.y}
            fontSize={eq.bold ? '10' : '9'}
            fontWeight={eq.bold ? '700' : '400'}
            fontFamily={eq.mono ? "'Courier New',monospace" : "'Georgia',serif"}
            fill={eq.col}>{eq.text}</text>
        ))}

        <line x1={363} y1={234} x2={945} y2={234} stroke="#e8c89a" strokeWidth="0.8"/>

        {/* Forward kinematics */}
        <text x={368} y={250} fontSize="10" fontWeight="700" fill="#7a2e00">Forward Kinematics (wheel speeds → robot velocity):</text>
        {[
          { y:266, text:'Vx = (r/4) × (−ω_FL + ω_FR + ω_RL − ω_RR)', col:'#c45a10', mono:true },
          { y:281, text:'Vy = (r/4) × ( ω_FL + ω_FR + ω_RL + ω_RR)', col:'#2a6496', mono:true },
          { y:296, text:'ω  = (r/4) × (−ω_FL + ω_FR − ω_RL + ω_RR) / (L+W)', col:'#2a7a3b', mono:true },
        ].map((eq, i) => (
          <text key={i} x={368} y={eq.y}
            fontSize="9.5" fontWeight="700"
            fontFamily="'Courier New',monospace"
            fill={eq.col}>{eq.text}</text>
        ))}

        <line x1={363} y1={310} x2={945} y2={310} stroke="#e8c89a" strokeWidth="0.8"/>

        {/* Notes */}
        {[
          { y:324, text:'Assumes: ideal rollers (no slip), 45° roller angle, all wheels same radius' },
          { y:337, text:'Singularity: None — system is fully actuated (4 wheels, 3 DOF)' },
          { y:350, text:'Max velocity limited by motor speed: V_max = ω_motor_max × r' },
        ].map((n, i) => (
          <text key={i} x={368} y={n.y} fontSize="8.5" fill="#7a4a28">{n.text}</text>
        ))}
      </svg>
      <div style={S.dCaption}>
        The inverse kinematics matrix maps directly from desired robot motion (Vx, Vy, ω) to individual wheel speeds. The (L+W) term captures how rotation requires differential wheel speeds proportional to the wheel-to-CoM distance.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Omni vs Mecanum vs Conventional comparison matrix
// Heatmap-style performance comparison
// ─────────────────────────────────────────────────────────────────────
function DiagramComparison() {
  const W = 960, H = 360;

  const criteria = [
    'Holonomic (any direction)',
    'Load capacity',
    'Traction / grip',
    'Efficiency (power loss)',
    'Floor surface tolerance',
    'Mechanical complexity',
    'Cost',
    'Positional accuracy',
    'Speed (straight-line)',
    'Lateral force capacity',
    'Incline performance',
    'Maintenance ease',
  ];

  // Scores 0–4: 0=Poor, 1=Fair, 2=Good, 3=Very Good, 4=Excellent
  const wheelTypes = [
    {
      name: 'Conventional\n(fixed axle)',
      scores: [0, 4, 4, 4, 4, 4, 4, 2, 4, 0, 4, 4],
      col: '#7a4a28',
    },
    {
      name: 'Omni\n(3-wheel)',
      scores: [4, 2, 2, 3, 2, 3, 2, 3, 3, 2, 1, 3],
      col: '#2a7a3b',
    },
    {
      name: 'Omni\n(4-wheel)',
      scores: [4, 3, 2, 3, 2, 2, 2, 4, 3, 3, 1, 3],
      col: '#2a6496',
    },
    {
      name: 'Mecanum\n(4-wheel)',
      scores: [4, 3, 2, 2, 1, 1, 1, 4, 3, 4, 0, 2],
      col: '#c45a10',
    },
    {
      name: 'Swerve Drive\n(complex)',
      scores: [4, 4, 4, 3, 3, 0, 0, 4, 4, 4, 3, 1],
      col: '#a04010',
    },
  ];

  const labels = ['Poor', 'Fair', 'Good', 'V.Good', 'Excel.'];
  const colors = ['#c0392b', '#d4874a', '#e8b07a', '#7a9e6a', '#2a7a3b'];

  const rowY = 38, rowH = (H - rowY - 40) / criteria.length;
  const labelW = 192, colW = (W - labelW - 10) / wheelTypes.length;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Wheel Technology Comparison Matrix — Holonomic vs Conventional Drive Systems</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>

        {/* Column headers */}
        {wheelTypes.map((wt, j) => {
          const x = labelW + j * colW + colW / 2;
          const lines = wt.name.split('\n');
          return (
            <g key={j}>
              <rect x={labelW + j*colW + 2} y={2} width={colW-4} height={rowY-4} rx={3}
                fill={wt.col} opacity="0.15"/>
              <rect x={labelW + j*colW + 2} y={2} width={colW-4} height={rowY-4} rx={3}
                fill="none" stroke={wt.col} strokeWidth="1.5"/>
              {lines.map((line, li) => (
                <text key={li} x={x} y={12 + li*14} textAnchor="middle"
                  fontSize="8.5" fontWeight="700" fill={wt.col}>{line}</text>
              ))}
            </g>
          );
        })}

        {/* Rows */}
        {criteria.map((crit, i) => {
          const y = rowY + i * rowH;
          return (
            <g key={i}>
              {/* Row label */}
              <rect x={0} y={y} width={labelW-4} height={rowH-2}
                fill={i%2===0 ? '#fdf4ec' : '#fdf6ee'} stroke="#e8c89a" strokeWidth="0.6"/>
              <text x={labelW-8} y={y + rowH/2 + 3.5} textAnchor="end"
                fontSize="9" fontWeight="700" fill="#7a2e00">{crit}</text>

              {/* Cells */}
              {wheelTypes.map((wt, j) => {
                const score = wt.scores[i];
                const x = labelW + j * colW;
                return (
                  <g key={j}>
                    <rect x={x+2} y={y+1} width={colW-4} height={rowH-3}
                      fill={colors[score]} rx={2} opacity="0.72"/>
                    <text x={x + colW/2} y={y + rowH/2 + 3.5} textAnchor="middle"
                      fontSize="8" fontWeight="700" fill="#fff">{labels[score]}</text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Legend */}
        {colors.map((c, i) => (
          <g key={i}>
            <rect x={labelW + i*90} y={H-28} width={12} height={12} fill={c} rx={2} opacity="0.8"/>
            <text x={labelW + i*90 + 16} y={H-18} fontSize="8.5" fill="#7a2e00">{labels[i]}</text>
          </g>
        ))}
        <text x={labelW + 5*90 + 20} y={H-18} fontSize="8" fontStyle="italic" fill="#7a4a28">← for each performance criterion</text>
      </svg>
      <div style={S.dCaption}>
        Mecanum and omni wheels trade efficiency and traction for holonomic motion. Swerve drive achieves the best all-round performance but at dramatically higher mechanical complexity and cost.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Wheel Configuration Layouts (top-down schematics)
// Shows standard roller orientations and naming conventions
// ─────────────────────────────────────────────────────────────────────
function DiagramMecanumLayout() {
  const W = 960, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mecanum Wheel Layout — Correct Roller Orientation for Standard 4-Wheel Drive</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', display:'block'}}>
        <defs>
          <marker id="arLY" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c45a10"/>
          </marker>
        </defs>

        {/* Two layouts side by side: X-config and O-config */}
        {[
          {
            ox: 220, oy: 150, label: 'X-Configuration (recommended)',
            sublabel: 'Roller diagonals form X pattern · most common',
            // FL=\, FR=/, RL=/, RR=\  (viewed from above)
            wheels: [
              { x:-70, y:-70, ra: Math.PI/4,    lbl:'FL (\\)', col:'#c45a10' },
              { x: 70, y:-70, ra:-Math.PI/4,    lbl:'FR (/)',  col:'#2a6496' },
              { x:-70, y: 70, ra:-Math.PI/4,    lbl:'RL (/)',  col:'#2a6496' },
              { x: 70, y: 70, ra: Math.PI/4,    lbl:'RR (\\)', col:'#c45a10' },
            ],
          },
          {
            ox: 700, oy: 150, label: 'O-Configuration (alternative)',
            sublabel: 'Roller diagonals form O pattern · less common',
            // FL=/, FR=\, RL=\, RR=/
            wheels: [
              { x:-70, y:-70, ra:-Math.PI/4,    lbl:'FL (/)',  col:'#2a6496' },
              { x: 70, y:-70, ra: Math.PI/4,    lbl:'FR (\\)', col:'#c45a10' },
              { x:-70, y: 70, ra: Math.PI/4,    lbl:'RL (\\)', col:'#c45a10' },
              { x: 70, y: 70, ra:-Math.PI/4,    lbl:'RR (/)',  col:'#2a6496' },
            ],
          },
        ].map((cfg, ci) => {
          const { ox, oy } = cfg;
          return (
            <g key={ci}>
              {/* Title */}
              <text x={ox} y={18} textAnchor="middle" fontSize="10" fontWeight="700" fill="#7a2e00">{cfg.label}</text>
              <text x={ox} y={30} textAnchor="middle" fontSize="8" fill="#a04010">{cfg.sublabel}</text>

              {/* Chassis */}
              <rect x={ox-100} y={oy-95} width={200} height={190}
                rx={8} fill="#d4874a" stroke="#7a2e00" strokeWidth="2" opacity="0.2"/>
              <text x={ox} y={oy+5} textAnchor="middle" fontSize="9" fontWeight="700" fill="#7a2e00">FRONT ↑</text>

              {/* Wheels */}
              {cfg.wheels.map((w, wi) => {
                const wx = ox + w.x, wy = oy + w.y;
                const wW = 24, wH = 42;
                const tc = Math.cos(w.ra), ts = Math.sin(w.ra);
                // Draw elongated wheel body
                const corners = [
                  [wx - wW/2, wy - wH/2],
                  [wx + wW/2, wy - wH/2],
                  [wx + wW/2, wy + wH/2],
                  [wx - wW/2, wy + wH/2],
                ].map(([x,y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
                return (
                  <g key={wi}>
                    <polygon points={corners}
                      fill={w.col} stroke="#7a2e00" strokeWidth="1.8" opacity="0.8"/>
                    {/* Roller diagonal line showing orientation */}
                    <line
                      x1={wx - tc*wW*0.6} y1={wy - ts*wW*0.6}
                      x2={wx + tc*wW*0.6} y2={wy + ts*wW*0.6}
                      stroke="#fdf0e4" strokeWidth="2.5"/>
                    <text x={wx} y={wy + wH/2 + 13} textAnchor="middle"
                      fontSize="8" fontWeight="700" fill="#7a2e00">{w.lbl}</text>
                  </g>
                );
              })}

              {/* Diagonal lines showing the X or O pattern */}
              <line x1={ox-70} y1={oy-70} x2={ox+70} y2={oy+70}
                stroke="#c45a10" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.5"/>
              <line x1={ox+70} y1={oy-70} x2={ox-70} y2={oy+70}
                stroke="#c45a10" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.5"/>
            </g>
          );
        })}

        {/* Separator */}
        <line x1={480} y1={10} x2={480} y2={H-10} stroke="#e8b07a" strokeWidth="1" strokeDasharray="5,4"/>

        {/* Bottom note */}
        <rect x={280} y={H-40} width={400} height={30} rx={4} fill="#fdf0e4" stroke="#c45a10" strokeWidth="1.2"/>
        <text x={480} y={H-24} textAnchor="middle" fontSize="8.5" fontWeight="700" fill="#7a2e00">
          Critical: wrong roller orientation = robot moves diagonally instead of straight
        </text>
      </svg>
      <div style={S.dCaption}>
        The X-configuration is standard: viewed from above, the four roller diagonals form an X shape. Swapping any two wheels without mirroring their roller direction will produce unexpected motion.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────
export default function MultidirectionalWheels() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Multidirectional Wheels in Mechanical Engineering</h1>
      <p style={S.p}>
        <strong>Multidirectional wheels</strong> — primarily <em>omni wheels</em> and <em>mecanum wheels</em> — enable ground vehicles to move
        in any direction without rotating the chassis. This property, called <strong>holonomic motion</strong>, is achieved by embedding
        freely-rotating rollers into the wheel rim at specific angles. The rollers allow passive lateral motion while the motor-driven
        hub provides active thrust in the perpendicular direction.
      </p>
      <p style={S.p}>
        These wheels are found wherever precise, unconstrained positioning is required in confined spaces: factory AGVs, hospital equipment,
        competition robots, and conveyor transfer stations. Understanding their geometry, kinematics, and limitations is essential
        for any engineer designing mobile robotic or material-handling systems.
      </p>

      <h2 style={S.h2}>Omni Wheel Anatomy</h2>
      <DiagramOmniAnatomy />
      <Table2 rows={[
        ['Operating principle', 'Barrel rollers mounted tangentially around the rim spin freely on their own axis\nDrive direction: perpendicular to roller axis (motor torque → forward)\nFree-roll direction: along roller axis (zero frictional resistance laterally)\nResult: wheel is actuated in one DOF, passive in the other'],
        ['Roller geometry', 'Roller axis tangent to wheel rim — exactly 90° to drive direction\nBarrel (convex) profile ensures single point contact with floor regardless of rotation angle\nContactcontinuity: at any hub angle, at least one roller is in contact with the ground\nMore rollers → smoother rolling, more floor contact points, less vibration'],
        ['Contact dynamics', 'Single roller contacts floor at any given instant (except at transitions)\nLoad concentrated on one roller → lower load capacity than solid wheel\nDouble-row design (two staggered rings) improves contact continuity\n"Thumping" characteristic: periodic bump as contact switches between rollers'],
        ['Roller materials', 'Hard polyurethane: low deformation, good durability, low rolling resistance\nSoft rubber: better grip on smooth floors, more deformation, higher friction\nNylon: low cost, lightweight, suitable for light loads and clean environments\nChoice depends on: required grip, floor surface, load, speed, and noise tolerance'],
        ['Variants', 'Single-row: simplest construction, most common, one line of rollers\nDouble-row: two offset rows ensure continuous ground contact, smoother, heavier\nDouble-disc: two sub-wheels on same axle, rollers span between — very smooth\n45° omni: rollers at 45° to rim — unusual, changes force coupling geometry'],
        ['Limitations', 'Low traction compared to conventional wheels — rollers slip easily laterally\nCannot generate lateral forces → requires multiple wheels to control direction\nLimited load capacity per wheel due to single-roller contact\nPoor performance on rough, uneven, or soft floors (rollers dig in or skip)'],
      ]}/>

      <h2 style={S.h2}>Mecanum Wheel Anatomy</h2>
      <DiagramMecanumAnatomy />
      <Table2 rows={[
        ['Operating principle', 'Rollers set at 45° to the wheel spin axis decompose drive force into two equal components\nF_forward = F_drive × cos(45°) = 0.707 × F_drive\nF_lateral  = F_drive × sin(45°) = 0.707 × F_drive\nFour wheels combine these components vectorially to produce net motion in any direction'],
        ['Left/Right variants', 'Left-hand wheel: rollers angled as \\ when viewed from above (top-right to bottom-left)\nRight-hand wheel: rollers angled as / when viewed from above (top-left to bottom-right)\nA four-wheel mecanum robot requires exactly 2 left and 2 right wheels\nWrong pairing: robot moves diagonally instead of straight — most common assembly mistake'],
        ['Force decomposition', 'For forward motion: all 4 wheels forward → lateral components cancel, forward components add\nFor lateral motion: diagonal wheel pairs reverse → forward components cancel, lateral add\nFor rotation: same-side wheels opposite → produce torque about vertical axis\nAny combination of Vx, Vy, ω simultaneously achievable (full holonomy)'],
        ['Floor requirements', 'Requires flat, hard, clean surface for effective operation\nRough floors: rollers catch on surface irregularities → reduced efficiency and control\nCarpet: high rolling resistance on rollers, poor lateral slip → mecanum ineffective\nWet/oily floors: roller grip reduced → loss of traction and control accuracy\nTypical: smooth concrete, epoxy-coated, smooth tile, or hardwood floors'],
        ['Efficiency vs omni', 'Only 70.7% of each wheel\'s drive force contributes to net motion (cos/sin 45° split)\nRemaining 29.3% is cancelled by opposing wheels in most motion modes\nOverall drive efficiency approximately 70–80% of equivalent conventional wheel drive\nHeating: motors run at higher current for equivalent motion speed vs standard drive'],
        ['Stall force', 'Maximum lateral force limited by roller friction coefficient × normal load\nRollers have relatively low grip → easily overwhelmed by side loads or slopes\nCannot hold position on lateral inclines — will slide if not actively controlled\nFor holding: motors must actively command wheels to resist slip; brake on stop'],
      ]}/>

      <h2 style={S.h2}>Mecanum Drive Motion Modes</h2>
      <DiagramMecanumModes />
      <Table2 rows={[
        ['Forward / reverse', 'All four wheels spin in same direction at same speed\nLateral force components cancel symmetrically → pure forward/reverse motion\nStraight-line motion: easiest to control, highest efficiency of all mecanum motions\nTorque: τ_net = 4 × τ_wheel × r / R_chassis'],
        ['Lateral (strafing)', 'Diagonal wheel pairs run in opposite directions\nFL+RR run forward, FR+RL run reverse (or vice versa for left)\nForward components cancel, lateral components add → pure sideways motion\nEfficiency ~50% lower than straight forward motion — motors fight each other'],
        ['Rotation in place', 'Left-side wheels run opposite to right-side wheels\nNo net translation → pure rotation about chassis centre\nUseful for precise heading control without changing position\nTurn radius effectively zero — not possible with conventional drive'],
        ['Diagonal motion', 'Two adjacent wheels on one diagonal axis run, two others stopped or reversed\nProduces 45° motion without any chassis rotation\nIntermediate angles possible by mixing wheel speeds\nTrue diagonal: only 2 of 4 wheels propel → roughly half the thrust'],
        ['Combined motion', 'Simultaneous Vx, Vy, and ω commands fully supported (holonomic)\nEach wheel speed computed from inverse kinematics matrix\nPID loops on each wheel independently maintain commanded speed\nOdometry: integrate forward kinematics to track position; drifts without encoders'],
        ['Motion limitations', 'Cannot produce arbitrary forces — limited by maximum roller grip\nLateral forces limited to μ × N per roller (μ typically 0.3–0.6)\nHigh-speed cornering: centrifugal force exceeds roller grip → lateral slide\nWet/dusty floors dramatically reduce maximum lateral force'],
      ]}/>

      <h2 style={S.h2}>Omni Drive Configurations</h2>
      <DiagramOmniDrive />
      <Table2 rows={[
        ['3-wheel 120° config', 'Three omni wheels at 120° angular spacing around chassis\nHolonomic: can produce any (Vx, Vy, ω) combination\nAdvantage: minimum actuators, compact, simple chassis structure\nDisadvantage: asymmetric force capability — some directions require larger motor torques\nInverse kinematics: simpler 3×3 matrix (no redundancy)'],
        ['4-wheel 90° config', 'Four omni wheels at 0°, 90°, 180°, 270° positions\nSymmetric force capability in all directions\nHigher payload than 3-wheel (more contact points, distributed load)\nRedundant actuation — over-constrained; requires compliance or matched wheel speeds\nInverse kinematics: 4×3 system, pseudo-inverse or weighted least-squares solution'],
        ['3-wheel kinematics', 'ω1 = (1/r)(−Vx sin(30°) + Vy cos(30°) + L·ωz)\nω2 = (1/r)(−Vx sin(150°) + Vy cos(150°) + L·ωz)\nω3 = (1/r)(−Vx sin(270°) + Vy cos(270°) + L·ωz)\nL = distance from chassis centre to wheel contact point'],
        ['Holonomic condition', 'A drive system is holonomic if its number of independently controllable DOF equals its total DOF\nConventional 2-wheel diff drive: 3 DOF (x,y,θ) but only 2 controlled → non-holonomic\nOmni/mecanum: 3 DOF, all independently controllable → holonomic\nHolonomic advantage: can move to any pose without path planning around non-holonomic constraints'],
        ['Practical considerations', 'Wheel placement tolerance critical: angular position error causes motion errors\nAll wheels must contact floor simultaneously — chassis must be level or compliant\nLoad sharing: with 4 wheels, floor flatness critical; shims or spring-loaded mounts help\nEncoder resolution determines positioning accuracy — higher counts for tighter control'],
      ]}/>

      <h2 style={S.h2}>Drive Kinematics</h2>
      <DiagramKinematics />
      <Table2 rows={[
        ['Inverse kinematics', 'Maps desired robot velocity (Vx, Vy, ω) to required wheel angular velocities\nUsed in real-time control loop: joystick/command → IK → motor speed setpoints\nMecanum IK is a linear transformation: [ω_wheels] = (1/r) × M × [Vx, Vy, ω]^T\nMatrix M encodes the geometric relationships (roller angle, wheel positions)'],
        ['Forward kinematics', 'Maps measured wheel velocities → estimated robot velocity (odometry)\nUsed for position tracking: integrate V_robot over time → pose estimate\nAccumulates error: wheel slip, roller slip, encoder noise all contribute to drift\nFor accurate positioning: supplement with external sensors (lidar, cameras, IMU)'],
        ['Wheel slip', 'Roller slip: lateral forces exceed roller static friction → wheel slides sideways\nDrive slip: high acceleration or deceleration → hub loses traction\nSlip causes IK assumptions to fail → actual motion ≠ commanded motion\nDetection: compare encoder-based odometry with IMU acceleration data'],
        ['Motor torque requirements', 'τ_motor = (F_load × r) / (η_drivetrain)\nMecanum: worst-case is lateral motion — all motors work against each other\nEach wheel motor must supply full lateral force independently\nOversizing motors by 1.5–2× recommended to handle dynamic loads and acceleration'],
        ['PID wheel control', 'Each wheel runs independent PID velocity controller (closed-loop)\nSetpoint: ω_commanded from IK · feedback: encoder-measured ω\nTuning: Kp, Ki, Kd set per wheel — identical wheels may need different gains\nFeedforward term (voltage/current) greatly improves response and reduces steady-state error'],
        ['Position control', 'Outer loop: position error → velocity command (P or PD controller)\nInner loop: velocity command → wheel speed (PID)\nCascaded control gives smooth, accurate trajectory following\nFull state feedback (LQR) used in high-performance systems with state estimation'],
      ]}/>

      <h2 style={S.h2}>Wheel Technology Comparison</h2>
      <DiagramComparison />
      <Table2 rows={[
        ['Conventional drive', 'Differential drive (2 driven wheels + castor): simplest, cheapest, robust\nCar-like (Ackermann): good straight-line, poor tight turns, no lateral motion\nAll terrain: high traction, rough surface capable, poor precision indoors\nNon-holonomic: requires path planning to reach arbitrary poses'],
        ['Omni drive advantages', 'True holonomic motion with standard round wheels (no steering mechanism)\nSmooth motion planning: can move in straight lines to any pose simultaneously\nNo dead-zones: any direction available at any heading without reorienting\nSimple construction: no complex steering joints, fewer moving parts than swerve'],
        ['Mecanum advantages', 'Highest lateral force capacity of all holonomic wheel types\nCan carry heavier loads than 3-wheel omni (4 contact points, better stability)\nWell-established in industry: standardised sizes, suppliers, mounting patterns\nGood speed performance: lateral motion faster than omni due to better roller geometry'],
        ['Swerve drive', 'Each wheel module can steer independently (0–360°) while driving\nBest performance: full traction in all directions, highest load capacity\nCost and complexity: each module requires 2 motors + steering encoder\nUsed in: FRC competition robots, high-end AGVs, research platforms'],
        ['Selection guidelines', 'Need low cost + any direction: omni (3-wheel) · simpler motion with 3 DOF control\nNeed lateral force + heavy load: mecanum 4-wheel · best efficiency/cost trade-off\nNeed best performance: swerve drive · if budget and complexity allow\nNeed rough terrain + omni: spherical/ball wheels (experimental, very low load)\nNeed flat floor: mecanum is first choice · rough floor: swerve or omni if smooth enough'],
        ['Failure modes', 'Mecanum: worn rollers → loss of lateral force → straight motion only (safe fail)\nOmni: broken roller → increased vibration and load on adjacent rollers → eventual hub failure\nBoth: floor contamination (oil, water, dust) → dramatic loss of traction and control accuracy\nEncoder failure: loss of closed-loop control → unpredictable motion; add watchdog/timeout'],
      ]}/>

      <h2 style={S.h2}>Mecanum Wheel Layout</h2>
      <DiagramMecanumLayout />
      <Table2 rows={[
        ['X vs O configuration', 'X-config: FL and RR have same roller angle (\\ ), FR and RL have same angle (/ )\nViewed from above: diagonals form an X shape — standard, most documented\nO-config: FL and RR have / rollers, FR and RL have \\ rollers\nBoth work — choose one and document clearly to avoid assembly errors'],
        ['Assembly verification', 'Quick check: lay all 4 wheels flat on a table in chassis positions\nX-config: rollers of FL and RR point same direction, FR and RL point opposite\nAlternative: spin each wheel forward — robot should move straight forward\nDiagonal motion when driving straight = left/right wheel swap error'],
        ['Wheel numbering conventions', 'No universal standard — always document your own convention clearly\nCommon: FL=1, FR=2, RL=3, RR=4 (counter-clockwise from front-left)\nSoftware: label motor controllers and encoders to match wheel numbering\nInversion: some vendors define positive as clockwise, others counter-clockwise — check datasheet'],
        ['Mounting hardware', 'Wheel hub: keyed shaft, D-shaft, or hex hub · match to motor gearbox output\nBearing support: overhung load on motor shaft — add support bearing where possible\nFasteners: high-cycle vibration environment — use thread-lock compound (Loctite 243)\nClearance: ensure rollers clear chassis frame and other components at all rotation angles'],
        ['Maintenance schedule', 'Rollers: inspect weekly for wear, cracking, or deformation under load\nHub bearings: lubricate monthly or per manufacturer spec · replace on rough running\nRoller axle pins/screws: check torque monthly — vibration loosens these frequently\nFloor contact surface: clean floor debris from rollers before each operating session'],
      ]}/>

      <h2 style={S.h2}>Engineering Applications</h2>
      <Table2 rows={[
        ['Industrial AGVs', 'Automated guided vehicles in warehouses: mecanum standard for narrow aisles\nAdvantage: lateral repositioning without reversing or complex path planning\nLoad: up to 5000 kg on large industrial mecanum platforms (custom designs)\nNavigation: laser SLAM or fixed beacon systems compensate for wheel slip odometry error'],
        ['Hospital & medical', 'Hospital bed movers: mecanum wheels allow lateral positioning in tight corridors\nImaging equipment (MRI, CT gantry): precise lateral positioning for scan alignment\nPharmacy robots: lateral sorting and shelving without aisle turn-around space\nRequirement: smooth hospital floors critical — cleaning protocol essential for roller life'],
        ['Competition robotics', 'FRC (FIRST Robotics): mecanum standard mid-range drive; swerve for high performance\nRoboCup: omni-wheel holonomic bases standard in most leagues\nResearch platforms: TurtleBot, various ROS platforms use 4-wheel omni\nKey trade-off: mecanum cheaper/simpler than swerve but lower traction for defense'],
        ['Conveyor & material handling', 'Omni wheels in conveyor transfer stations: route packages without full divert mechanisms\nPallet movers: mecanum wheels allow precise lateral docking to loading bays\nAssembly line positioning tables: omni wheel turntables for part orientation\nAirport baggage handling: curved conveyor junctions use omni roller arrays'],
        ['Assistive technology', 'Powered wheelchairs: omni-based designs allow lateral movement and rotation in tight spaces\nDimensions critical: IEC 62133 and ADA clearances require precise positional control\nSafety: passive fail-safe — if power lost, rollers lock or restrict lateral motion\nBattery life: holonomic motion increases motor current draw 20–40% vs straight-line drive'],
        ['Limitations in practice', 'Soft/uneven floors (carpet, gravel, outdoor): both wheel types fail — use swerve or tracks\nHeavy inclines: lateral grip insufficient to hold position on >5° slopes (mecanum)\nHigh-speed dynamics: roller inertia causes vibration at high RPM; balance wheel assemblies\nCleanliness: production environments with chips, coolant, or debris rapidly degrade roller life'],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Mecanum IK — FL:  ω_FL = (1/r)(Vx − Vy − (L+W)·ω)",
        "Mecanum IK — FR:  ω_FR = (1/r)(Vx + Vy + (L+W)·ω)",
        "Mecanum IK — RL:  ω_RL = (1/r)(Vx + Vy − (L+W)·ω)",
        "Mecanum IK — RR:  ω_RR = (1/r)(Vx − Vy + (L+W)·ω)",
        "Force per roller:  F_roller = τ_motor / (r × cos45°)  for mecanum lateral",
        "Holonomic condition: #controllable_DOF = #total_DOF  (3 for ground robot)",
        "3-wheel omni IK:  ωi = (1/r)(−Vx·sin(θi) + Vy·cos(θi) + L·ωz)",
        "Mecanum efficiency (lateral):  η = cos(45°) = 0.707  per wheel contribution",
        "Drive force (forward): F = 4 × τ_motor / r  (all wheels, no cancellation)",
        "Drive force (lateral):  F = 4 × τ_motor × cos(45°) / r  (effective ~2.83 wheels)",
        "Roller contact angle (omni): α = 0°  (tangential)  |  (mecanum): α = 45°",
        "Max load capacity limited by: N = min(roller_load_rating × n_rollers_in_contact)",
      ]}/>
    </div>
  );
}